import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useEventCatalog, type EventCatalogLocality } from '@/hooks/useEventCatalog';
import { useEventFull } from '@/hooks/useEventFull';
import { useInventory } from '@/hooks/useInventory';
import { PartnerBenefits, FcPointsBadge } from '@/components/PartnerBenefits';
import { useSession } from '@/lib/auth';
import { SeatPicker, SeatMapSection, type SeatPickerSelection } from '@/components/SeatPicker';
import { getSeatMap } from '@/lib/api/taquilla';
import { Badge, Button, Card, CardTitle, Skeleton, SkeletonGroup, toast } from '@/components/ui';
import { useCart } from '@/store/cart';
import { formatEventDate } from '@/lib/format';

/**
 * Event page — detalle de evento.
 *
 * ROUND9 UAT-C10: "ver el evento" está desacoplado de "comprar". La
 * metadata viene de `useEventCatalog()` (`public.events`, siempre legible
 * para un evento publicado) y SOLO un 404 real del evento muestra el
 * error duro. Las localidades / inventario vienen por separado de
 * `useInventory()` (`taquilla/inventory`); si están vacías o fallan, el
 * evento igual se renderiza con un estado digno "Entradas próximamente"
 * en vez de romper la página entera.
 */
export function EventPage(): JSX.Element {
  const { t, i18n } = useTranslation(['events', 'common']);
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const cart = useCart();
  const { user, loading: sessionLoading } = useSession();

  const catalog = useEventCatalog(eventId);
  const inventory = useInventory(eventId);
  // F5 — mapa de asientos (geometría + estado por silla). Si el evento tiene
  // localidades con sillas, se compra POR SILLA sobre el mapa; si no, cae al
  // SeatPicker de lista (cupo / admisión general).
  const seatMapQ = useQuery({
    queryKey: ['seat-map', eventId],
    queryFn: () => getSeatMap(eventId as string),
    enabled: !!eventId,
    staleTime: 30_000,
  });
  // Composición §6: beneficios del partner de First Class. De presentación —
  // se hidrata aparte y degrada de forma digna si no hay partner / falla.
  const partnerFull = useEventFull(eventId);

  /**
   * ROUND35 add-on (FC-029 web / FC-025 app) — deep-link contract
   * `add-to-cart`. Web/App generan URLs:
   *
   *   /event/<eventId>?intent=add-to-cart&locality=<localityId>&qty=<n>
   *
   * Comportamiento (spec Tech Lead):
   *   - intent !== 'add-to-cart' → flujo normal de página de detalle.
   *   - intent === 'add-to-cart' + no logueado → redirect a /login con
   *     `from=<pathname + search>` para retomar post-login.
   *   - locality presente + válido + qty válido → auto-addLine + toast
   *     "Agregado al carrito · N boleta(s)". El user SE QUEDA en /event/{id}.
   *   - locality no presente → quedarse en la página; el SeatPicker es
   *     la fallback natural (UX scroll-to-picker queda implícito porque
   *     no hay redirect/modal extra).
   *   - locality inválido (no existe en inventory) → toast error +
   *     fallback al selector.
   *   - qty > stock → clamp + toast warning.
   *
   * Idempotencia: usamos un `useRef` flag + cleanup de la query string
   * vía `setSearchParams({}, {replace: true})` después de procesar, así
   * un reload o navegación back no re-dispara el auto-add.
   *
   * R35 cross-platform alignment: aceptamos `?intent=add-to-cart` (contrato
   * canónico web) Y `?action=add-to-cart` (App-lead lo emitió pre-RESPONSE
   * con ese nombre — backwards-compat alias para no bloquear deploy app).
   */
  const intent = searchParams.get('intent') ?? searchParams.get('action');
  const intentLocality = searchParams.get('locality');
  const intentQtyRaw = searchParams.get('qty');
  const deeplinkProcessed = useRef(false);

  useEffect(() => {
    if (intent !== 'add-to-cart') return;
    if (!eventId) return;
    if (deeplinkProcessed.current) return;
    // Esperá a que la session resuelva. Sin esto: si la API tarda, el
    // primer pass tendría `user === null` y redirigiría a /login aunque
    // el user esté autenticado.
    if (sessionLoading) return;

    // No logueado → redirect a /login con `from` para retomar el intent.
    if (!user) {
      const fromPath = `${location.pathname}${location.search}`;
      deeplinkProcessed.current = true;
      navigate(`/login?from=${encodeURIComponent(fromPath)}`, { replace: true });
      return;
    }

    // localityId no presente → caer al selector manual silenciosamente
    // (el SeatPicker está abajo). Limpiamos la query INMEDIATAMENTE
    // (no esperamos inventory) para que la URL refleje el estado real.
    if (!intentLocality) {
      deeplinkProcessed.current = true;
      setSearchParams({}, { replace: true });
      return;
    }

    // A partir de acá necesitamos validar contra `inventory`.
    if (inventory.isLoading) return;

    if (inventory.isError || !inventory.data) {
      deeplinkProcessed.current = true;
      toast.error({
        title: 'No pudimos validar la localidad',
        description: 'Probá elegir la localidad manualmente.',
      });
      setSearchParams({}, { replace: true });
      return;
    }

    // Buscar la locality en el inventory cargado.
    let foundStageId: string | null = null;
    let foundLocalityName = '';
    let foundStageName = '';
    let foundAvailable = 0;
    for (const stage of inventory.data.stages) {
      const loc = stage.localities.find((l) => l.localityId === intentLocality);
      if (loc) {
        foundStageId = stage.stageId;
        foundLocalityName = loc.name;
        foundStageName = stage.stageName;
        foundAvailable = loc.available;
        break;
      }
    }

    if (!foundStageId) {
      deeplinkProcessed.current = true;
      toast.error({
        title: 'Localidad no disponible',
        description: 'La localidad indicada en el link no existe en este evento.',
      });
      setSearchParams({}, { replace: true });
      return;
    }

    // qty: default 1, clamp a max(1, min(stock, 10)).
    const qtyParsed = intentQtyRaw ? Number.parseInt(intentQtyRaw, 10) : 1;
    const desiredQty = Number.isFinite(qtyParsed) && qtyParsed > 0 ? qtyParsed : 1;
    const maxQty = Math.max(1, Math.min(foundAvailable, 10));
    const finalQty = Math.min(desiredQty, maxQty);
    const clamped = finalQty < desiredQty;

    deeplinkProcessed.current = true;
    cart.setEvent(eventId);
    cart.addLine({
      eventId,
      stageId: foundStageId,
      localityId: intentLocality,
      seatIdentifier: null,
      quantity: finalQty,
    });
    if (clamped) {
      toast.info({
        title: 'Cantidad ajustada al stock disponible',
        description: `Agregamos ${finalQty}× ${foundLocalityName} (pediste ${desiredQty}, quedaban ${foundAvailable}).`,
      });
    } else {
      toast.success({
        title: `Agregado al carrito · ${finalQty} boleta${finalQty === 1 ? '' : 's'}`,
        description: `${foundLocalityName} (${foundStageName})`,
      });
    }
    setSearchParams({}, { replace: true });
  }, [
    intent,
    intentLocality,
    intentQtyRaw,
    eventId,
    user,
    sessionLoading,
    inventory.isLoading,
    inventory.isError,
    inventory.data,
    cart,
    navigate,
    location,
    setSearchParams,
  ]);

  // Catálogo de precios para el SeatPicker — derivado del inventario real
  // (`taquilla/inventory`), keyed por localityId.
  const localityCatalog = useMemo<Map<string, EventCatalogLocality>>(() => {
    const map = new Map<string, EventCatalogLocality>();
    for (const stage of inventory.data?.stages ?? []) {
      for (const loc of stage.localities) {
        map.set(loc.localityId, {
          id: loc.localityId,
          stageId: stage.stageId,
          name: loc.name,
          priceFullCents: loc.priceFullCents ?? null,
          pricePromoCents: loc.pricePromoCents ?? null,
        });
      }
    }
    return map;
  }, [inventory.data]);

  function handleAdd(selection: SeatPickerSelection): void {
    if (!eventId) return;
    // ROUND21 #2 — `setEvent` ya no resetea `lines`; sólo deja al cart
    // saber qué evento se está navegando (para el botón "Volver al evento"
    // en /cart). El `eventId` viaja también en la propia line, lo cual le
    // permite al cart acumular boletas de varios eventos a la vez.
    cart.setEvent(eventId);
    cart.addLine({
      eventId,
      stageId: selection.stageId,
      localityId: selection.localityId,
      seatIdentifier: null, // general admission per locality; specific seats post-H7
      quantity: selection.quantity,
    });
    toast.success({
      title: 'Agregado al carrito',
      description: `${selection.quantity}× ${selection.localityName} (${selection.stageName})`,
    });
  }

  if (!eventId) {
    return (
      <section className="container-fc py-12">
        <p className="text-fc-muted">Falta el identificador del evento.</p>
      </section>
    );
  }

  // ---- Loading state ----
  if (catalog.isLoading) {
    return (
      <section className="container-fc py-12">
        <SkeletonGroup label="Cargando evento" className="flex flex-col gap-4">
          <Skeleton shape="h-10 w-2/3" />
          <Skeleton shape="h-4 w-1/2" />
          <Skeleton shape="h-40 w-full" />
        </SkeletonGroup>
      </section>
    );
  }

  // ---- Catalog failure — SOLO si el evento mismo no existe / falla ----
  if (catalog.isError || !catalog.data) {
    const status = (catalog.error as { status?: number } | null)?.status;
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">
          {status === 404 ? 'Evento no encontrado' : 'No pudimos cargar este evento'}
        </h1>
        <p className="mt-2 text-fc-muted">
          {status === 404
            ? 'Verificá el link o probá desde la página principal.'
            : 'Probá refrescar en unos segundos.'}
        </p>
        <Button className="mt-6" onClick={() => navigate('/')} variant="secondary">
          Volver al inicio
        </Button>
      </section>
    );
  }

  const event = catalog.data.event;
  // Ola 3 — título/descripción localizados con fallback al español base.
  const lang = (i18n.language || 'es').toLowerCase().slice(0, 2);
  const tr = event.translations?.[lang];
  const localizedTitle = tr?.title?.trim() || event.title;
  const localizedDescription = tr?.description?.trim() || event.description;
  const cartCount = cart.lines.reduce((sum, l) => sum + l.quantity, 0);
  const stages = inventory.data?.stages ?? [];
  const hasStages = stages.length > 0;
  // F5 — ¿el evento tiene asientos numerados? (localidades con sillas en el mapa)
  const seatedLocalities = (seatMapQ.data?.localities ?? []).filter(
    (l) => (l.seats?.length ?? 0) > 0,
  );
  const hasSeatMap = seatedLocalities.length > 0;
  // El carrito de taquilla compra por stage_id; el evento tiene una etapa de
  // venta (la usamos para todas las líneas por-silla).
  const eventStageId = stages[0]?.stageId ?? null;

  return (
    <section className="container-fc py-8 md:py-12">
      <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl">{localizedTitle}</h1>
          {event.categoryName && (
            <Badge tone="neutral" className="mt-2">
              {event.categoryName}
            </Badge>
          )}
          <p className="mt-2 text-fc-muted">{formatEventDate(event.date)}</p>
          {(event.venue || event.cityName) && (
            <p className="text-fc-muted">
              {[event.venue, event.cityName].filter(Boolean).join(' · ')}
            </p>
          )}
          {partnerFull.data?.grantsFcPoints && (
            <div className="mt-3">
              <FcPointsBadge />
            </div>
          )}
        </div>
        {cartCount > 0 && (
          <Button onClick={() => navigate('/cart')}>
            {t('common:nav.cartWithCount', { count: cartCount })}
          </Button>
        )}
      </header>

      {localizedDescription && (
        <p className="mb-8 max-w-3xl text-base text-fc-ink">{localizedDescription}</p>
      )}

      <h2 className="mb-4 font-display text-2xl">{t('events:detail.tickets')}</h2>

      {/* ---- Sección de compra: degrada de forma digna ---- */}
      {inventory.isLoading || seatMapQ.isLoading ? (
        <SkeletonGroup label="Cargando disponibilidad" className="flex flex-col gap-3">
          <Skeleton shape="h-4 w-40" />
          <Skeleton shape="h-32 w-full" />
        </SkeletonGroup>
      ) : hasSeatMap ? (
        /* F5 — compra POR SILLA sobre el mapa de asientos (Arena USC). */
        <SeatMapSection
          seatMap={seatMapQ.data!}
          eventId={eventId}
          stageId={eventStageId}
          currency={event.currency}
        />
      ) : hasStages ? (
        <>
          <Card variant="ghost" padded={false} className="mb-6 flex items-center gap-3 px-4 py-3">
            <Badge tone="info">Actualizado cada 5s</Badge>
            <span className="text-sm text-fc-muted">
              Snapshot{' '}
              <time dateTime={inventory.data?.snapshotAt}>
                {inventory.data?.snapshotAt
                  ? new Date(inventory.data.snapshotAt).toLocaleTimeString('es-CO')
                  : '—'}
              </time>
            </span>
          </Card>
          <SeatPicker
            inventory={inventory.data!}
            localityCatalog={localityCatalog}
            onAdd={handleAdd}
            currency={event.currency}
            eventId={eventId}
          />
        </>
      ) : inventory.isError ? (
        <Card variant="outlined" padded>
          <CardTitle>Disponibilidad no disponible</CardTitle>
          <p className="mt-2 text-fc-muted">
            No pudimos cargar la disponibilidad de entradas en este momento. El evento sigue acá —
            probá refrescar la disponibilidad en unos minutos.
          </p>
          <Button
            className="mt-4"
            variant="secondary"
            onClick={() => {
              void inventory.refetch();
            }}
          >
            Reintentar
          </Button>
        </Card>
      ) : (
        <Card variant="outlined" padded>
          <CardTitle>Entradas próximamente</CardTitle>
          <p className="mt-2 text-fc-muted">
            Las entradas para este evento todavía no están a la venta. Volvé pronto para conseguir
            las tuyas.
          </p>
        </Card>
      )}

      {/* Beneficios del partner de First Class (composición §6) */}
      <PartnerBenefits composition={partnerFull.data ?? null} loading={partnerFull.isLoading} />
    </section>
  );
}
